import { createSignal, mergeProps, onMount } from "solid-js";
import cn from "classnames";
import { ImgixImage } from "#components/imgixImage";
import { FullScreenGallery } from "#components/legacy/fullScreenGallery";
import { Expand } from "#icons";
import { Controls } from "./controls";

export interface SliderProps {
  images: {
    alt: string;
    caption: string;
    credit: string;
    src: string;
    width: number;
    height: number;
    title: string;
  }[];
  className?: string;
  polite?: boolean;
}

const getScrollPosition = (elem: HTMLUListElement) => {
  const { offsetWidth, scrollLeft, scrollWidth } = elem || {};
  const tail = scrollWidth - offsetWidth - scrollLeft;

  return {
    pageWidth: offsetWidth,
    head: scrollLeft,
    tail: Math.max(0, tail),
  };
};

export function Slider(props: SliderProps) {
  props = mergeProps(
    {
      polite: false,
      images: [],
    },
    props
  );

  // Render nothing if there are no images
  if (props.images.length < 1) return null;

  const [hasScroll, setHasScroll] = createSignal(false);
  const [sliderRef, setSliderRef] = createSignal<HTMLUListElement>();
  const [activeChild, setActiveChild] = createSignal(0);
  const [scrollPosition, setScrollPosition] = createSignal({
    pageWidth: 0,
    head: 0,
    tail: 0,
  });
  const [galleryOpen, setGalleryOpen] = createSignal(false);

  // Update scroll position and active child index
  const handleScrollUpdate = () => {
    const slider = sliderRef();
    if (!slider) return;

    const position = getScrollPosition(slider);
    setScrollPosition(position);

    const { head, pageWidth } = position;
    if (pageWidth > 0) {
      setActiveChild(Math.round(head / pageWidth));
    }
  };

  const checkScroll = () => {
    const slider = sliderRef();
    if (!slider) return;

    const { scrollWidth, clientWidth } = slider;
    setHasScroll(scrollWidth > clientWidth);
  };

  const handleScroll = (leftScroll: number) => {
    sliderRef()?.scrollTo({ left: leftScroll, behavior: "smooth" });
  };

  const handleNext = () => {
    const { head, tail, pageWidth } = getScrollPosition(sliderRef()!);
    const leftScrollDistance =
      tail >= pageWidth ? head + pageWidth : head + tail;
    handleScroll(leftScrollDistance);
    setActiveChild(prev => Math.min(prev + 1, props.images.length - 1));
  };

  const handlePrev = () => {
    const { head, pageWidth } = getScrollPosition(sliderRef()!);
    const leftScrollDistance = head >= pageWidth ? head - pageWidth : 0;
    handleScroll(leftScrollDistance);
    setActiveChild(prev => Math.max(prev - 1, 0));
  };

  onMount(() => {
    checkScroll();
    handleScrollUpdate(); // Ensure scroll position is updated on mount
    window.addEventListener("resize", checkScroll);

    const slider = sliderRef();
    if (slider) {
      slider.addEventListener("scroll", handleScrollUpdate);
    }

    return () => {
      window.removeEventListener("resize", checkScroll);
      if (slider) {
        slider.removeEventListener("scroll", handleScrollUpdate);
      }
    };
  });

  return (
    <div
      class={cn(
        "w-full max-w-full overflow-x-hidden space-y-4",
        props.className
      )}
    >
      <ul
        ref={setSliderRef}
        class="flex overflow-x-scroll md:overflow-x-auto overflow-y-hidden gap-x-4 snap-mandatory snap-x scroll-smooth no-scrollbar bg-sand-100 h-full"
        {...(props.polite ? { "aria-live": "polite" } : {})}
      >
        {props.images.map(({ src, alt, width, height }) => {
          const isPortrait = Number(height) > Number(width);
          return (
            <li class="w-full shrink-0 snap-start flex flex-col gap-y-4">
              <div
                class={cn("2xl:container 2xl:px-0", {
                  "relative w-full h-full flex items-center": isPortrait,
                })}
              >
                <ImgixImage
                  src={src}
                  alt={alt}
                  className={cn("object-contain aspect-video", {
                    "w-full": !isPortrait,
                    "h-full mx-auto w-auto": isPortrait,
                  })}
                  width={isPortrait ? Number(width) : 1400}
                  height={isPortrait ? Number(height) : 650}
                  imgixParams={{
                    ...(isPortrait
                      ? {
                          w: "auto",
                          h: 1600,
                        }
                      : {}),
                    fit: "crop",
                  }}
                  srcSet={`${src}?w=320 320w, ${src}?w=640 640w, ${src}?w=1024 1024w, ${src}?w=1400 1400w`}
                  sizes={
                    isPortrait
                      ? "(max-width: 1920px) 50vw, 45vw"
                      : "(max-width: 1920px) 100vw, 90vw"
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>

      {(props.images[activeChild()].caption || hasScroll()) && (
        <div
          class={cn(
            "container flex flex-wrap items-center mx-auto space-y-4 sm:flex-nowrap xl:max-w-6xl sm:space-y-0"
          )}
        >
          {props.images[activeChild()].caption && (
            <p class="w-full text-xs md:mx-12 grow sm:w-auto h-14 sm:h-auto">
              {props.images[activeChild()].caption}
            </p>
          )}
          {hasScroll() && (
            <div
              class={cn(
                "w-full flex justify-between items-center sm:w-content sm:gap-6"
              )}
            >
              <Controls
                onNext={handleNext}
                onPrev={handlePrev}
                scrollPosition={scrollPosition}
                totalItemsCount={sliderRef()?.children.length || 0}
                activeItem={() => activeChild() + 1}
              />
              <button
                class="flex items-center h-10 btn btn-primary sm:hidden"
                onClick={() => setGalleryOpen(true)}
                data-tagular={JSON.stringify({
                  event: "ElementClicked",
                  actionOutcome: "open",
                  outboundUrl: "null",
                  webElement: {
                    location: "hero",
                    position: "gallery",
                    text: "show photos",
                    elementType: "button",
                  },
                })}
              >
                <Expand className="inline-flex mr-2" /> Expand
              </button>

              {galleryOpen() && (
                <FullScreenGallery
                  currentImageIndex={activeChild()}
                  images={props.images}
                  handleExit={() => setGalleryOpen(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
